import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import apiService from '../services/api';

// Import static fallback images from src/assets/
import heroImg1 from '../assets/ramcharan-visit.jpeg';
// import heroImg2 from '../assets/WhatsApp Image 2026-07-09 at 5.12.40 PM (4).jpeg';
// import heroImg3 from '../assets/WhatsApp Image 2026-07-09 at 5.12.41 PM (2).jpeg';

// Import other images
import schoolImg from '../assets/school-pq1xlys3txvs05u6o2u6u5k2t7ne0ao8wwcdaiublo.png';
import vocationalImg from '../assets/Vocational-pq1xkqlowq7auhmcu1qq2ypew3p4yisv2tojqintq4.png';
import mobileEyeImg from '../assets/mobile-eye-care-pq1xkw8q1qf0s5e5x46hhxa6gexc8pf93llgm6fgos.png';
import healthImg from '../assets/Health-checkups-pq1xktf7h8b5tbi9dkylsfzso9b8lm4237n06cjn7g.png';
import collegeImg from '../assets/College-pq1xky4efehlfdbfm4zqmwt3n6o2o3mpruwfkqcocc.png';
import hostelImg from '../assets/Hostel-pq1xkrjj3k8l63kzok5cnggvhhki67wleyc17smfjw.png';

// Import about strip images
import diningHall from '../assets/dining-hall.jpeg';
import ramcharanVisit from '../assets/ramcharan-visit.jpeg';

const HERO_STATS = [
  ['5000+', 'Lives Transformed'],
  ['670+', 'Free Boarding'],
  ['36', 'Years of Service'],
  ['100%', 'Free of Cost'],
];

const OFFERS = [
  { img: schoolImg, title: 'Devnar School', desc: 'Structured academics built for visually challenged learners, from primary grade onward.' },
  { img: vocationalImg, title: 'Vocational Training Center', desc: 'Skills that open real employment paths after graduation.' },
  { img: mobileEyeImg, title: 'Mobile Eye Checkup', desc: 'Free eye-care camps that reach rural communities directly.' },
  { img: healthImg, title: 'Medical Checkups', desc: 'Ongoing health monitoring for every student, year-round.' },
  { img: collegeImg, title: 'Junior College', desc: 'Continued education pathways beyond school into higher study.' },
  { img: hostelImg, title: 'Devnar Hostel', desc: 'Safe, free residential care with meals four times a day.' },
];

const PILLARS = [
  { title: 'Gift of Quality Education', desc: 'Devnar has been providing quality education to over 500 visually challenged children for more than three decades. Help us spread the light of education to many more.' },
  { title: 'Gift of Good Residential Facility', desc: 'Devnar has residential facilities for its school students, providing clean, secure stay and wholesome nutritious meals four times a day.' },
  { title: 'Gift of Sports and Applicative Learning', desc: 'Devnar students have long displayed brilliance in sports, and Devnar was the first school in India to offer computer education to the visually challenged.' },
  { title: 'Gift of Regular Medical Support', desc: 'Devnar Foundation offers regular medical check-ups and monitoring to its students and the rural poor.' },
];

const STATS = [
  ['5000+', 'Students'],
  ['300+', 'Awards & Accolades'],
  ['100%', 'Success in Academics'],
  ['500+', 'Boarding Facilities'],
];

const ABOUT_STRIPS = [
  {
    img: diningHall,
    alt: 'Founders with students in the Devnar dining hall',
    eyebrow: 'Reach Out',
    title: 'Rebuild Lives, One Child at a Time',
    text: "Hold hands of those little ones who are seeking to build rainbows in darkness. There are different ways of volunteering — from homemakers to students to professionals, anyone with an empathetic heart can become a volunteer at Devnar.",
    cta: 'Become a Volunteer',
    href: '/volunteer',
    reverse: false,
  },
  {
    img: ramcharanVisit,
    alt: 'Actor Ram Charan visits Devnar Foundation',
    eyebrow: 'Celebrities & Wellwishers',
    title: 'Standing With Our Children',
    text: 'Devnar Foundation is loved and supported by leaders, artists and citizens across India. From film stars to spiritual leaders to ministers — every visit strengthens the light we carry for our students.',
    cta: 'Explore the Gallery',
    href: '/gallery',
    reverse: true,
  },
];

const WAYS_TO_HELP = [
  {
    title: 'Sponsor Student',
    desc: 'Support a visually impaired student\'s education, including tuition, books, and daily needs.',
    href: '/donate?type=sponsor-student',  // Changed to query param
    icon: 'fa-graduation-cap'
  },
  {
    title: 'Sponsorship Donation',
    desc: 'Make a sponsorship donation to support our programs and infrastructure for the blind.',
    href: '/donate?type=sponsorship-donation',  // Changed to query param
    icon: 'fa-hand-holding-heart'
  },
  {
    title: 'Food Donation',
    desc: 'Provide nutritious meals to our students and help us maintain the mid-day meal program.',
    href: '/donate?type=food-donation',  // Changed to query param
    icon: 'fa-utensils'
  }
];

const IconArrow = ({ dir = 1 }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: dir < 0 ? 'rotate(180deg)' : 'none' }}>
    <polyline points="9 6 15 12 9 18" />
  </svg>
);

/* ----------------------------------------------------------------
   Reveal-on-scroll wrapper
---------------------------------------------------------------- */
function useOnScreen(ref, threshold = 0.18) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return visible;
}

function Reveal({ as: Tag = 'div', className = '', delay = 0, children, ...rest }) {
  const ref = useRef(null);
  const visible = useOnScreen(ref);
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? 'reveal-visible' : ''} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* Count-up numeral used in the stats sections */
function CountStat({ value, label }) {
  const ref = useRef(null);
  const visible = useOnScreen(ref, 0.4);
  const match = value.match(/^(\d+)(.*)$/);
  const target = match ? parseInt(match[1], 10) : 0;
  const suffix = match ? match[2] : '';
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!visible) return;
    const reduceMotion = typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) { setDisplay(target); return; }

    let raf;
    const duration = 1100;
    let start = null;
    const step = (ts) => {
      if (start === null) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.floor(eased * target));
      if (progress < 1) raf = requestAnimationFrame(step);
      else setDisplay(target);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [visible, target]);

  return (
    <div ref={ref}>
      <strong>{display}{suffix}</strong>
      <span>{label}</span>
    </div>
  );
}

/* ----------------------------------------------------------------
   Main component
---------------------------------------------------------------- */
export default function Home() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [parallax, setParallax] = useState(0);
  const [awards, setAwards] = useState([]);
  const [awardsLoading, setAwardsLoading] = useState(true);
  const [banners, setBanners] = useState([]);
  const [bannersLoading, setBannersLoading] = useState(true);
  const [websiteInfo, setWebsiteInfo] = useState(null);
  const scrollerRef = useRef(null);

  // Fetch website info
  useEffect(() => {
    const fetchWebInfo = async () => {
      try {
        const result = await apiService.fetchWebinfo();
        if (result.status === 'success' && result.data && result.data.length > 0) {
          setWebsiteInfo(result.data[0]);
        }
      } catch (error) {
        console.error('Error fetching web info:', error);
      }
    };
    fetchWebInfo();
  }, []);

  // Fetch awards data using apiService
  useEffect(() => {
    const fetchAwards = async () => {
      try {
        const result = await apiService.fetchAwards();
        
        if (result.success && result.data && result.data.length > 0) {
          const formattedAwards = result.data.map(item => ({
            img: item.image,
            alt: item.title_en,
            year: item.award_date ? item.award_date.split('-')[0] : 'N/A',
            title: item.title_en,
            description: item.description_en,
            id: item.id
          }));
          setAwards(formattedAwards);
        } else {
          setAwards([]);
        }
      } catch (error) {
        console.error('Error fetching awards:', error);
        setAwards([]);
      } finally {
        setAwardsLoading(false);
      }
    };

    fetchAwards();
  }, []);

  // Fetch banners data
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const result = await apiService.fetchBanners();
        
        if (result.status === 'success' && result.data && result.data.length > 0) {
          const validBanners = result.data.filter(banner => banner.image && banner.image.trim() !== '');
          
          if (validBanners.length > 0) {
            const bannerImages = validBanners.map(banner => banner.image);
            setBanners(bannerImages);
          } else {
            setBanners([heroImg1]);
          }
        } else {
          setBanners([heroImg1]);
        }
      } catch (error) {
        console.error('Error fetching banners:', error);
        setBanners([heroImg1]);
      } finally {
        setBannersLoading(false);
      }
    };

    fetchBanners();
  }, []);

  // hero parallax
  useEffect(() => {
    const onScroll = () => {
      setParallax(Math.min(window.scrollY * 0.25, 120));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // autoplay hero slider
  useEffect(() => {
    if (bannersLoading || banners.length === 0) return;
    
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;
    
    const id = setInterval(() => setActiveSlide((a) => (a + 1) % banners.length), 5500);
    return () => clearInterval(id);
  }, [banners, bannersLoading]);

  const scrollAwards = useCallback((dir) => {
    scrollerRef.current?.scrollBy({ left: dir * 320, behavior: 'smooth' });
  }, []);

  const hasAwards = awards.length > 0;
  const heroSlides = banners.length > 0 ? banners : [heroImg1];
  const orgName = websiteInfo?.app_name || 'Devnar Foundation for the Blind';

  if (bannersLoading) {
    return (
      <>
        <Header />
        <div className="devnar">
          <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div>Loading...</div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="devnar">
        <a className="skip-link" href="#main">Skip to main content</a>

        {/* ---------------- Hero ---------------- */}
        <section className="hero" aria-label="Devnar Foundation highlights">
          <div className="hero-slider" style={{ transform: `translateY(${parallax * 0.15}px)` }}>
            {heroSlides.map((src, i) => (
              <div
                key={`banner-${i}`}
                className={`slide ${i === activeSlide ? 'active' : ''}`}
                style={{ backgroundImage: `url('${src}')` }}
                aria-hidden={i !== activeSlide}
              />
            ))}
          </div>
          <div className="hero-content">
            <span className="eyebrow">Since 1988 · Padma Shri Awarded</span>
            <h1>Let there be Light.</h1>
            <p className="kicker">Devnar Foundation gives free education, hostel, meals and lifelong dignity to more than 670 visually challenged children — three decades of building rainbows in the dark.</p>
            <div className="hero-buttons">
              <Link className="btn btn-primary" to="/donate">❤ Donate Now</Link>
              <Link className="btn btn-outline btn-outline-light" to="/about">Our Story →</Link>
            </div>
          </div>
          <div className="hero-stats">
            {HERO_STATS.map(([value, label]) => (
              <div key={label}><strong>{value}</strong><span>{label}</span></div>
            ))}
          </div>
        </section>

        <main id="main" className="wrap page-content">

          {/* ---------------- What we do ---------------- */}
          <section>
            <Reveal className="section-title" as="div">
              <span className="eyebrow">What We Do</span>
              <h2>Devnar Offers</h2>
              <div className="rule" />
            </Reveal>
            <Reveal className="lede" as="p" delay={80}>
              Devnar has been providing quality education to over 500 visually challenged children for more than three decades.
            </Reveal>
            <div className="offer-grid">
              {OFFERS.map((o, i) => (
                <Reveal as="div" className="offer" key={o.title} delay={i * 70}>
                  <img src={o.img} alt={o.title} />
                  <h3>{o.title}</h3>
                  <p>{o.desc}</p>
                </Reveal>
              ))}
            </div>
          </section>

          {/* ---------------- Awards ---------------- */}
          {!awardsLoading && hasAwards && (
            <section className="awards-section">
              <div className="wrap">
                <Reveal className="section-title" as="div">
                  <span className="eyebrow">National Recognition</span>
                  <h2>Honoured by the Nation</h2>
                  <div className="rule" />
                </Reveal>
                <Reveal className="lede" as="p" delay={80}>
                  From Presidents of India to the Prime Minister — the work of Devnar Foundation has been celebrated at the highest offices of the country.
                </Reveal>
              </div>

              <div className="awards-carousel">
                <button className="carousel-btn prev" aria-label="Previous awards" onClick={() => scrollAwards(-1)}><IconArrow dir={-1} /></button>
                <div className="awards-showcase" ref={scrollerRef}>
                  {awards.map((a) => (
                    <div className="award-card" key={a.id || a.title}>
                      <img src={a.img} alt={a.alt} loading="lazy" />
                      <div className="caption">
                        <span className="year">{a.year}</span>
                        <h4>{a.title}</h4>
                      </div>
                    </div>
                  ))}
                </div>
                <button className="carousel-btn next" aria-label="Next awards" onClick={() => scrollAwards(1)}><IconArrow dir={1} /></button>
              </div>

              <div className="wrap" style={{ textAlign: 'center', marginTop: 24 }}>
                <Link className="btn btn-outline btn-outline-light" to="/awards">View all awards →</Link>
              </div>
            </section>
          )}

          {/* ---------------- CTA band ---------------- */}
          <Reveal as="section" className="cta-band">
            <h2>Gift a light of dignified future to visually challenged children.</h2>
            <Link className="btn btn-primary" to="/donate">Donate Now</Link>
          </Reveal>

          {/* ---------------- Four gifts ---------------- */}
          <section>
            <Reveal className="section-title" as="div">
              <span className="eyebrow">Your Gift Becomes</span>
              <h2>Four Gifts of Devnar</h2>
              <div className="rule" />
            </Reveal>
            <div className="pillars">
              {PILLARS.map((p, i) => (
                <Reveal as="div" className="pillar" key={p.title} delay={i * 70}>
                  <h3>{p.title}</h3>
                  <p>{p.desc}</p>
                </Reveal>
              ))}
            </div>
          </section>

          {/* ---------------- Stats ---------------- */}
          <section className="dot-field stats-wrap">
            <div className="stats">
              {STATS.map(([value, label]) => (
                <CountStat key={label} value={value} label={label} />
              ))}
            </div>
          </section>

          {/* ---------------- About strips ---------------- */}
          {ABOUT_STRIPS.map((s) => (
            <Reveal as="section" className={`about-strip ${s.reverse ? 'reverse' : ''}`} key={s.title}>
              <img src={s.img} alt={s.alt} loading="lazy" />
              <div>
                <span className="eyebrow">{s.eyebrow}</span>
                <h2>{s.title}</h2>
                <p>{s.text}</p>
                <Link className="btn btn-primary" to={s.href}>{s.cta}</Link>
              </div>
            </Reveal>
          ))}

          {/* ---------------- Ways to Help ---------------- */}
          <section className="ways-section">
            <div className="container">
              <div className="section-header">
                <span className="eyebrow">Get Involved</span>
                <h2>Ways You Can Help</h2>
                <div className="divider">
                  <span className="divider-line"></span>
                  <span className="divider-diamond">◆</span>
                  <span className="divider-line"></span>
                </div>
                <p className="section-subtitle">
                  Every contribution makes a difference in the lives of visually impaired individuals.
                  Choose the way that speaks to your heart.
                </p>
              </div>

              <div className="ways-grid">
                {WAYS_TO_HELP.map((way, index) => (
                  <div 
                    className="way-card" 
                    key={way.title}
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="way-icon">
                      <i className={`fas ${way.icon}`}></i>
                    </div>
                    <h3>{way.title}</h3>
                    <p>{way.desc}</p>
                    <Link to={way.href} className="way-link">
                      <span>Donate Now</span>
                      <i className="fas fa-arrow-right"></i>
                    </Link>
                  </div>
                ))}
              </div>

              <div className="ways-cta">
                <p>
                  <strong>{orgName}</strong> is a registered NGO with 12A &amp; 80G tax exemptions.
                  Every donation is eligible for tax benefits under section 80G.
                </p>
                <Link to="/contact" className="btn btn-outline">
                  <i className="fas fa-envelope"></i> Contact Us for More Ways to Help
                </Link>
              </div>
            </div>
          </section>
        </main>
      </div>
      <Footer />
    </>
  );
}